// Bloc HTML brut pour les tutoriels
// L'admin saisit du HTML dans l'éditeur, il est rendu tel quel sur la page publique
// et dans les exports (HTML / PDF).
import { useState } from 'react';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import { Code2, Eye, Trash2 } from 'lucide-react';
import './html-block.css';

function HtmlBlockView({ node, updateAttributes, editor, deleteNode }) {
  const html = node.attrs.html || '';
  const [mode, setMode] = useState(html ? 'preview' : 'code');

  // Page publique / lecture seule : uniquement le rendu
  if (!editor.isEditable) {
    if (!html.trim()) return null;
    return (
      <NodeViewWrapper className="tut-html-block-rendered">
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper className="tut-html-block" contentEditable={false}>
      <div className="tut-html-bar">
        <span className="tut-html-badge">HTML</span>
        <div className="tut-html-tabs">
          <button
            type="button"
            className={mode === 'code' ? 'is-active' : ''}
            onClick={() => setMode('code')}
            title="Éditer le code HTML"
          >
            <Code2 size={13} /> Code
          </button>
          <button
            type="button"
            className={mode === 'preview' ? 'is-active' : ''}
            onClick={() => setMode('preview')}
            title="Aperçu du rendu"
          >
            <Eye size={13} /> Aperçu
          </button>
        </div>
        <button
          type="button"
          className="tut-html-del"
          onClick={() => deleteNode?.()}
          title="Supprimer le bloc"
        >
          <Trash2 size={13} />
        </button>
      </div>

      {mode === 'code' ? (
        <textarea
          className="tut-html-src"
          value={html}
          spellCheck={false}
          placeholder={'<div class="ma-classe">\n  Collez votre HTML ici…\n</div>'}
          onChange={(e) => updateAttributes({ html: e.target.value })}
          onKeyDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onPaste={(e) => e.stopPropagation()}
        />
      ) : (
        <div className="tut-html-preview">
          {html.trim() ? (
            <div dangerouslySetInnerHTML={{ __html: html }} />
          ) : (
            <p className="tut-html-empty">Bloc vide — passez en « Code » pour saisir du HTML.</p>
          )}
        </div>
      )}
    </NodeViewWrapper>
  );
}

export const HtmlBlock = Node.create({
  name: 'htmlBlock',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      html: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-html') || '',
        renderHTML: (attributes) => ({ 'data-html': attributes.html || '' }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-html-block]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-html-block': '', class: 'html-block' })];
  },

  addNodeView() {
    return ReactNodeViewRenderer(HtmlBlockView);
  },

  addCommands() {
    return {
      insertHtmlBlock: (html = '') => ({ commands }) =>
        commands.insertContent({ type: this.name, attrs: { html } }),
    };
  },
});

export default HtmlBlockView;
